import { useCallback, useMemo } from 'react';

import {
  INVESTMENT_OPPORTUNITIES_EVENTS,
  INVESTMENT_OPPORTUNITIES_PAGE,
  INVESTMENT_OPPORTUNITIES_PLATFORM,
  INVESTMENT_OPPORTUNITIES_SOURCES,
  TAssetType,
  TTrack,
  useAnalytics,
} from '@r-client/shared/data/analytics';

/**
 * Domain analytics hook for the /investment-opportunities page. Mirrors
 * `useUsOnboardingAnalytics`.
 *
 * Every event on this page goes through one of these methods, so `source` and
 * `platform` are attached in exactly one place and no call site ever assembles a
 * params literal. `investor_id` is not passed manually — Segment `identify()`
 * attaches it at login, so events inherit it.
 */
export function useInvestmentOpportunitiesAnalytics() {
  const analytics = useAnalytics();

  const trackEvent = useCallback(
    (event: TTrack) => {
      analytics.track(event);
    },
    [analytics]
  );

  return useMemo(() => {
    const meta = {
      source: INVESTMENT_OPPORTUNITIES_SOURCES.PAGE,
      platform: INVESTMENT_OPPORTUNITIES_PLATFORM,
    } as const;

    return {
      /**
       * Call from the page, not from `InvestmentOpportunities` — the component
       * is a library export and a view is a routing fact, not a mount fact.
       */
      pageViewed: () => analytics.page(INVESTMENT_OPPORTUNITIES_PAGE),

      assetCardClicked: (
        assetType: TAssetType,
        slug: string,
        filter?: string,
        sort?: string
      ) =>
        trackEvent({
          name: INVESTMENT_OPPORTUNITIES_EVENTS.ASSET_CARD_CLICKED,
          params: {
            asset_type: assetType,
            filter_category: filter,
            slug,
            sort,
            ...meta,
          },
        }),

      seeAllClicked: (assetType: TAssetType) =>
        trackEvent({
          name: INVESTMENT_OPPORTUNITIES_EVENTS.SEE_ALL_CLICKED,
          params: { asset_type: assetType, ...meta },
        }),

      assetFollowed: (
        assetType: TAssetType,
        slug: string,
        filter?: string,
        sort?: string
      ) =>
        trackEvent({
          name: INVESTMENT_OPPORTUNITIES_EVENTS.ASSET_FOLLOWED,
          params: {
            asset_type: assetType,
            filter_category: filter,
            slug,
            sort,
            ...meta,
          },
        }),

      assetUnfollowed: (
        assetType: TAssetType,
        slug: string,
        filter?: string,
        sort?: string
      ) =>
        trackEvent({
          name: INVESTMENT_OPPORTUNITIES_EVENTS.ASSET_UNFOLLOWED,
          params: {
            asset_type: assetType,
            filter_category: filter,
            slug,
            sort,
            ...meta,
          },
        }),
    };
  }, [analytics, trackEvent]);
}
